import React from "react";
import styled from "@emotion/styled";
import { useSelector } from "react-redux";
import { ITooltipParams } from "ag-grid-community";
import { isProgressRow, selectRetentionDays, selectStatusCountsEntry } from "../../store/statusCountsSlice";
import { RagMarker } from "./RagMarker";
import { RagBand } from "./ragBand";
import { ProgressUnavailable, describeStatusProgress } from "./statusProgress";

// colors.darkGrayishBlue from the Cozonac theme.
const Box = styled.div`
  width: 340px;
  background: #2e2e43;
  color: #fff;
  font-size: 12.5px;
  line-height: 1.5;
  padding: 10px 12px 11px;
  border-radius: 4px;
  box-shadow: 0 6px 12px rgba(46, 46, 67, 0.25);
`;

const Head = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
  font-size: 13px;
`;

const Bar = styled.div`
  height: 5px;
  border-radius: 3px;
  background: rgba(255, 255, 255, 0.18);
  margin: 7px 0 6px;
  overflow: hidden;
`;

const BarFill = styled.span<{ percent: number; colour: string }>`
  display: block;
  height: 100%;
  border-radius: 3px;
  width: ${({ percent }) => percent}%;
  background: ${({ colour }) => colour};
`;

const Muted = styled.span`
  color: #b9b9c8;
`;

const BarColours: Record<RagBand, string> = {
  green: "#3fb03f",
  amber: "#f1a71c",
  red: "#e8495a",
  resolving: "#6c92cb",
  none: "#9a9aab",
};

/**
 * Status-cell tooltip for Waiting / Notified / Failed rows. Facts only: counts, the first
 * resolved time, the as-of time and the retention caveat, plus the reason when there is no
 * icon. No action hints.
 */
export const StatusCountsTooltip = (params: ITooltipParams): JSX.Element | null => {
  const data = params.data as { id?: string; partitionKey?: string; status?: unknown } | undefined;
  const decorated = isProgressRow(data);
  const entry = useSelector(selectStatusCountsEntry(decorated ? data?.partitionKey : undefined));
  const retentionDays = useSelector(selectRetentionDays);

  if (!decorated) {
    return null;
  }

  const progress = describeStatusProgress(entry, retentionDays);

  if (progress.kind === "unavailable") {
    return (
      <Box data-testid="status-counts-tooltip" style={{ width: "auto" }}>
        {ProgressUnavailable}
      </Box>
    );
  }

  return (
    <Box data-testid="status-counts-tooltip">
      <Head>
        {progress.band !== "none" && <RagMarker band={progress.band} size={15} onDark />}
        {progress.headline}
      </Head>
      <Bar>
        <BarFill percent={progress.percent} colour={BarColours[progress.band]} />
      </Bar>
      {(progress.breakdown || progress.excluded) && (
        <div>
          {progress.breakdown}
          {progress.excluded && <Muted> {progress.excluded}</Muted>}
        </div>
      )}
      <div>{progress.timeline}</div>
      {progress.reason && (
        <div>
          <b>Why no icon:</b> {progress.reason}
        </div>
      )}
      {progress.caveat && (
        <div>
          <Muted>{progress.caveat}</Muted>
        </div>
      )}
    </Box>
  );
};
